import React from 'react';
import { PieChart, Pie, Sector, Cell, ResponsiveContainer } from 'recharts';
import Animate from 'react-smooth';
import { stdColors } from './scoreConstants.js';

export const grades = {
  65: {
    letter: 'C',
    oneWord: 'Needs Improvement',
    color: stdColors.red,
  },
  70: {
    letter: 'C+',
    oneWord: 'Needs Improvement',
    color: stdColors.orange,
  },
  75: {
    letter: 'B\u2013',
    oneWord: 'Okay',
    color: stdColors.orangeLight,
  },
  80: {
    letter: 'B',
    oneWord: 'Good',
    color: stdColors.yellow,
  },
  85: {
    letter: 'B+',
    oneWord: 'Good',
    color: stdColors.yellowLight,
  },
  90: {
    letter: 'A\u2013',
    oneWord: 'Great',
    color: stdColors.green,
  },
  95: {
    letter: 'A',
    oneWord: 'Excellent',
    color: stdColors.greenLight,
  },
  100: {
    letter: 'A+',
    oneWord: 'Superb',
    color: stdColors.greenNeon,
  },
};

export const roundGradeValue = value => {
  // These are offset intentionally
  if (value < 65) {
    return 65;
  } else if (value < 70) {
    return 70;
  } else if (value < 75) {
    return 75;
  } else if (value < 80) {
    return 80;
  } else if (value < 85) {
    return 85;
  } else if (value < 90) {
    return 90;
  } else if (value < 95) {
    return 95;
  } else if (value >= 95) {
    return 100;
  }
};

export const getGrade = value => {
  value = roundGradeValue(value);
  return grades[value];
};

export class LetterScore extends React.Component {
  state = {
    grade: null,
    isIncreasing: false,
    gradeAppearAnimation: null,
    initialAnimation: true,
    lastUpdateValue: null, //used for animation
  };

  fadeInDuration = 750;

  componentWillReceiveProps(nextProps) {
    if (this.props !== nextProps) {
      const { initialValue, updateValue } = nextProps;

      this.setState({
        lastUpdateValue: this.props.updateValue,
      });
    }
  }

  willComponentUpdate = (initialValue, updateValue) =>
    roundGradeValue(this.props.initialValue) !== roundGradeValue(initialValue) ||
    roundGradeValue(this.props.updateValue) !== roundGradeValue(updateValue);

  getData = () => {
    const { initialValue, updateValue } = this.props;

    const basicBarLength = updateValue ? Math.min(initialValue, updateValue) : initialValue;

    const downBarLength = updateValue ? Math.max(0, initialValue - updateValue) : 0;
    const upBarLength = updateValue ? Math.max(0, updateValue - initialValue) : 0;

    const extendedBarLength = 100 - (updateValue ? Math.max(updateValue, initialValue) : initialValue);

    const basicBarColor = getGrade(!!updateValue ? updateValue : initialValue).color;

    return [
      {
        value: basicBarLength,
        fill: basicBarColor,
      },
      {
        value: downBarLength,
        className: 'redFillChange',
      },
      {
        value: upBarLength,
        className: 'greenFillChange',
      },
      {
        value: extendedBarLength,
        fill: '#ececec',
      },
    ];
  };

  renderActiveShape = ({ cx, cy, value, letter, oneWord, innerRadius, outerRadius, startAngle, endAngle, fill }) => {
    const { initialValue, updateValue } = this.props;

    const grade = updateValue ? getGrade(updateValue) : getGrade(initialValue);

    const lastGrade = getGrade(this.state.lastUpdateValue ? this.state.lastUpdateValue : initialValue);

    const newGrade = lastGrade.letter != grade.letter;

    const wrappedGrade = grade.letter.split('');
    const wrappedText = grade.oneWord.split(' ');
    const isTitlePresent = !!this.props.title;

    const gradeFromAnimation = {
      opacity: newGrade ? 0 : 1,
      y: newGrade ? cy + innerRadius : cy + innerRadius * 0.4,
      labelY: newGrade ? cy + innerRadius / 2 : cy,
    };

    const gradeToAnimation = {
      opacity: 1,
      y: cy + innerRadius * 0.4,
      labelY: cy,
    };

    const oneWordFromAnimation = {
      opacity: newGrade ? 0 : 1,
      x: newGrade ? cx - 60 : cx,
    };

    const oneWordToAnimation = {
      opacity: 1,
      x: cx,
    };

    return (
      <g>
        <Sector
          fill={fill}
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
        />
        <Animate from={gradeFromAnimation} to={gradeToAnimation} duration={this.fadeInDuration} begin={0}>
          {({ opacity, y }) => (
            <text
              style={{ opacity }}
              x={cx}
              y={y}
              textAnchor="middle"
              fontSize={innerRadius * 1.5}
              fill={grade.color}
              fontFamily="Roboto"
            >
              {wrappedGrade[0]}
            </text>
          )}
        </Animate>
        <Animate from={gradeFromAnimation} to={gradeToAnimation} duration={this.fadeInDuration} begin={0}>
          {({ opacity, labelY }) => (
            <text
              style={{ opacity }}
              x={cx + innerRadius / 1.75}
              y={labelY}
              textAnchor="middle"
              fontSize={innerRadius / 2}
              fontWeight={500}
              fill={grade.color}
              fontFamily={'Roboto'}
            >
              {wrappedGrade[1]}
            </text>
          )}
        </Animate>
        <Animate from={oneWordFromAnimation} to={oneWordToAnimation} duration={this.fadeInDuration} begin={0}>
          {({ opacity, x }) => (
            <text
              style={{ opacity }}
              x={cx}
              y={cy}
              textAnchor="middle"
              fontSize={Math.max(innerRadius / 3, 12)}
              fill={grade.color}
            >
              {wrappedText.map((line, key) => (
                <tspan key={key} x={x} y={cy} dy={innerRadius / 1.1 + key * Math.max(innerRadius / 3, 12)}>
                  {line}
                </tspan>
              ))}
            </text>
          )}
        </Animate>
      </g>
    );
  };

  render() {
    const { title, initialValue, updateValue } = this.props;

    if (!initialValue) {
      return null;
    }

    const diameter = title ? 150 : 165;
    const data = this.getData();

    const arrowDown = !!updateValue && initialValue > updateValue;
    const arrowUp = !!updateValue && initialValue < updateValue;

    return (
      <div className="hrbr-score-holder w-100 h-100">
        <ResponsiveContainer width={'100%'} aspect={4 / 3}>
          <PieChart>
            <Pie
              dataKey="value"
              stroke="none"
              activeIndex={3}
              activeShape={this.renderActiveShape}
              data={data}
              cx={'50%'}
              cy={'50%'}
              innerRadius={'75%'}
              outerRadius={'100%'}
              startAngle={225}
              endAngle={-45}
              fill={'green'}
              animationBegin={0}
              animationDuration={this.fadeInDuration}
            >
              {data.map((entry, index) => <Cell key={index} class={entry.className} fill={entry.fill} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="test">
          {arrowUp && <div className="arrow-up" />}
          {arrowDown && <div className="arrow-down" />}
        </div>
      </div>
    );
  }
}

export default LetterScore;
